import React, { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useTranslation } from "react-i18next";
import { Box, Typography, TextField, Button, Alert, MenuItem } from "@mui/material";
import { alpha } from "@mui/material/styles";
import Send from "@mui/icons-material/Send";
import { submitContactForm } from "../api";
import { ContactFormData } from "../types";
import { submitStaffContactForm } from "../../staff-augmentation/api";
import Notification from "../../../components/ui/Notification";

const MIN_NOMBRE_LENGTH = 2;
const MIN_EMPRESA_LENGTH = 2;
const MIN_MENSAJE_LENGTH = 10;
const MAX_PHONE_LENGTH = 20;

interface Errors {
  name?: string;
  email?: string;
  companyName?: string;
  message?: string;
  [key: string]: string | undefined;
}

interface Touched {
  name?: boolean;
  email?: boolean;
  companyName?: boolean;
  message?: boolean;
  [key: string]: boolean | undefined;
}

type FormularioContactoProps = {
  variant?: "default" | "delivery";
};

const deliveryInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
  },
};

function DeliveryLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      sx={{
        display: "block",
        fontWeight: 700,
        color: "text.primary",
        fontSize: "0.875rem",
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

export default function FormularioContacto({
  variant = "default",
}: FormularioContactoProps) {
  const { t, i18n } = useTranslation();
  const captchaRef = useRef<ReCAPTCHA>(null);
  const isDelivery = variant === "delivery";

  const [token, setToken] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [nombreUsuario, setNombreUsuario] = useState("");
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    companyName: "",
    message: "",
    phone: "",
  });
  const [delivery, setDelivery] = useState({
    need: "",
    profiles: "",
    teamSize: "",
    timeline: "",
    extra: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const [touched, setTouched] = useState<Touched>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const needOptions = t("pages.staff.contact.need_options", {
    returnObjects: true,
  }) as string[];
  const teamSizeOptions = t("pages.staff.contact.team_size_options", {
    returnObjects: true,
  }) as string[];
  const timelineOptions = t("pages.staff.contact.timeline_options", {
    returnObjects: true,
  }) as string[];

  const isEmailValid = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const phoneError = () => {
    if (!phoneValue || phoneValue.trim() === "")
      return t("contacto_form.error_telefono");
    if (!isValidPhoneNumber(phoneValue))
      return t("contacto_form.error_telefono_formato");
    return "";
  };

  const validateField = (name: string, value: string) => {
    let error = "";
    switch (name) {
      case "name":
        if (value.trim().length < MIN_NOMBRE_LENGTH)
          error = t("contacto_form.error_nombre");
        break;
      case "email":
        if (!isEmailValid(value)) error = t("contacto_form.error_email");
        break;
      case "companyName":
        if (value.trim().length < MIN_EMPRESA_LENGTH)
          error = t("contacto_form.error_empresa");
        break;
      case "message":
        if (value.trim().length < MIN_MENSAJE_LENGTH)
          error = t("contacto_form.error_mensaje");
        break;
      case "need":
      case "teamSize":
      case "timeline":
        if (!value) error = t("pages.staff.contact.error_select");
        break;
      case "profiles":
        if (value.trim().length < 2)
          error = t("pages.staff.contact.error_profiles");
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const onChange = (value: string | null) => {
    setToken(value);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    let filteredValue = value;

    if (name === "name") {
      filteredValue = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: filteredValue }));

    if (touched[name]) {
      validateField(name, filteredValue);
    }
  };

  const handleDeliveryChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setDelivery((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const isFormValid = () => {
    const phoneErr = phoneError();
    const baseValid =
      formData.name.trim().length >= MIN_NOMBRE_LENGTH &&
      isEmailValid(formData.email) &&
      !phoneErr &&
      formData.companyName.trim().length >= MIN_EMPRESA_LENGTH &&
      (recaptchaSiteKey ? token !== null : true) &&
      Object.values(errors).every((err) => !err);

    if (isDelivery) {
      return (
        baseValid &&
        Boolean(delivery.need) &&
        delivery.profiles.trim().length >= 2 &&
        Boolean(delivery.teamSize) &&
        Boolean(delivery.timeline)
      );
    }

    return baseValid && formData.message.trim().length >= MIN_MENSAJE_LENGTH;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentErrors: Errors = {};
    if (formData.name.trim().length < MIN_NOMBRE_LENGTH)
      currentErrors.name = t("contacto_form.error_nombre");
    if (!isEmailValid(formData.email))
      currentErrors.email = t("contacto_form.error_email");
    if (formData.companyName.trim().length < MIN_EMPRESA_LENGTH)
      currentErrors.companyName = t("contacto_form.error_empresa");

    if (isDelivery) {
      if (!delivery.need)
        currentErrors.need = t("pages.staff.contact.error_select");
      if (delivery.profiles.trim().length < 2)
        currentErrors.profiles = t("pages.staff.contact.error_profiles");
      if (!delivery.teamSize)
        currentErrors.teamSize = t("pages.staff.contact.error_select");
      if (!delivery.timeline)
        currentErrors.timeline = t("pages.staff.contact.error_select");
    } else if (formData.message.trim().length < MIN_MENSAJE_LENGTH) {
      currentErrors.message = t("contacto_form.error_mensaje");
    }

    const phoneErr = phoneError();
    if (phoneErr) {
      setPhoneTouched(true);
    }

    if (Object.keys(currentErrors).length > 0 || phoneErr) {
      setErrors(currentErrors);
      setTouched((prev) => ({
        ...prev,
        ...Object.keys(formData).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {},
        ),
        need: true,
        profiles: true,
        teamSize: true,
        timeline: true,
      }));
      return;
    }

    if (recaptchaSiteKey && !token) {
      setSubmitError(t("contacto_form.captcha_required"));
      return;
    }

    setCargando(true);
    setSubmitError(null);
    setNombreUsuario(formData.name.trim());

    try {
      if (isDelivery) {
        await submitStaffContactForm(
          {
            name: formData.name.trim(),
            email: formData.email.trim(),
            companyName: formData.companyName.trim(),
            phone: phoneValue || "",
            need: delivery.need,
            profiles: delivery.profiles.trim(),
            teamSize: delivery.teamSize,
            timeline: delivery.timeline,
            extra: delivery.extra.trim(),
          },
          token,
        );
      } else {
        await submitContactForm(
          {
            name: formData.name.trim(),
            email: formData.email.trim(),
            companyName: formData.companyName.trim(),
            phone: phoneValue || "",
            message: formData.message.trim(),
          },
          token,
        );
      }
      setSuccessMessage(t("contacto_form.exito", { nombre: formData.name.trim() }));
      setEnviado(true);
    } catch (error: any) {
      const baseMessage = error.friendlyMessage || t("contacto_form.error");
      setSubmitError(baseMessage);
      setToken(null);
      if (captchaRef.current) captchaRef.current.reset();
    } finally {
      setCargando(false);
    }
  };

  if (enviado) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Notification
          message={successMessage}
          type="success"
          onClose={() => setSuccessMessage(null)}
        />
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "#0A1128", mt: 2 }}
        >
          {t("contacto_form.exito", { nombre: nombreUsuario })}
        </Typography>
      </Box>
    );
  }

  const currentPhoneError = phoneTouched ? phoneError() : "";

  const defaultFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
    },
  };

  return (
    <Box
      sx={(theme) =>
        isDelivery
          ? {
              bgcolor: "background.default",
              p: { xs: 3, md: 5 },
              borderRadius: 3,
              boxShadow: `0 12px 40px ${alpha(theme.palette.text.primary, 0.08)}`,
              border: "1px solid",
              borderColor: "divider",
            }
          : {
              bgcolor: "white",
              p: { xs: 4, md: 6 },
              borderRadius: "2rem",
              boxShadow: "0px 20px 50px rgba(10,17,40,0.05)",
              border: "1px solid #e2e8f0",
              position: "relative",
              overflow: "hidden",
            }
      }
    >
      {!isDelivery && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "8px",
              background: "linear-gradient(to right, #68a8de, #0A1128)",
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#0A1128",
              textAlign: "center",
              mb: 4,
              fontSize: { xs: "1.5rem", md: "1.875rem" },
            }}
          >
            {t("contacto_form.titulo")}
          </Typography>
        </>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>
          {submitError}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: isDelivery
            ? "grid"
            : "flex",
          flexDirection: isDelivery ? undefined : "column",
          gridTemplateColumns: isDelivery
            ? { xs: "1fr", sm: "1fr 1fr" }
            : undefined,
          gap: isDelivery ? 2.5 : 3,
        }}
      >
        {isDelivery ? (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
              <DeliveryLabel htmlFor="delivery-name">
                {t("pages.staff.contact.name")}
              </DeliveryLabel>
              <TextField
                id="delivery-name"
                fullWidth
                name="name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                disabled={cargando}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                required
                variant="outlined"
                sx={deliveryInputSx}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
              <DeliveryLabel htmlFor="delivery-company">
                {t("pages.staff.contact.company")}
              </DeliveryLabel>
              <TextField
                id="delivery-company"
                fullWidth
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.companyName && !!errors.companyName}
                helperText={touched.companyName && errors.companyName}
                disabled={cargando}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                required
                variant="outlined"
                sx={deliveryInputSx}
              />
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <DeliveryLabel htmlFor="delivery-email">
                {t("pages.staff.contact.email")}
              </DeliveryLabel>
              <TextField
                id="delivery-email"
                fullWidth
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                disabled={cargando}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                required
                variant="outlined"
                sx={deliveryInputSx}
              />
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <DeliveryLabel>{t("pages.staff.contact.phone")}</DeliveryLabel>
              <Box
                sx={(theme) => ({
                  p: "14px 16px",
                  border: "1px solid",
                  borderColor: currentPhoneError ? "error.main" : "divider",
                  borderRadius: 1,
                  bgcolor: "background.default",
                  "&:focus-within": {
                    borderColor: currentPhoneError
                      ? "error.main"
                      : "primary.main",
                    boxShadow: currentPhoneError
                      ? "none"
                      : `0 0 0 2px ${alpha(theme.palette.primary.main, 0.16)}`,
                  },
                  "& .PhoneInputInput": {
                    border: "none",
                    outline: "none",
                    fontSize: "1rem",
                    width: "100%",
                    marginLeft: "10px",
                    color: "text.primary",
                  },
                })}
              >
                <PhoneInput
                  international
                  defaultCountry="AR"
                  value={phoneValue}
                  onChange={(val) => setPhoneValue(val || "")}
                  onBlur={() => setPhoneTouched(true)}
                  disabled={cargando}
                  placeholder={t("contacto_form.placeholder_telefono")}
                  maxLength={MAX_PHONE_LENGTH}
                />
              </Box>
              {currentPhoneError && (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", mt: "4px", ml: "14px" }}
                >
                  {currentPhoneError}
                </Typography>
              )}
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <DeliveryLabel htmlFor="delivery-need">
                {t("pages.staff.contact.need")}
              </DeliveryLabel>
              <TextField
                id="delivery-need"
                select
                fullWidth
                name="need"
                value={delivery.need}
                onChange={handleDeliveryChange}
                onBlur={handleBlur}
                error={touched.need && !!errors.need}
                helperText={touched.need && errors.need}
                disabled={cargando}
                required
                variant="outlined"
                sx={{
                  ...deliveryInputSx,
                  "& .MuiSelect-select": {
                    color: delivery.need ? "text.primary" : "text.secondary",
                  },
                }}
                slotProps={{ select: { displayEmpty: true } }}
              >
                <MenuItem value="" disabled>
                  {t("pages.staff.contact.select_placeholder")}
                </MenuItem>
                {Array.isArray(needOptions) &&
                  needOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <DeliveryLabel htmlFor="delivery-profiles">
                {t("pages.staff.contact.profiles")}
              </DeliveryLabel>
              <TextField
                id="delivery-profiles"
                fullWidth
                name="profiles"
                placeholder={t("pages.staff.contact.profiles_placeholder")}
                value={delivery.profiles}
                onChange={handleDeliveryChange}
                onBlur={handleBlur}
                error={touched.profiles && !!errors.profiles}
                helperText={touched.profiles && errors.profiles}
                disabled={cargando}
                slotProps={{ htmlInput: { maxLength: 200 } }}
                required
                variant="outlined"
                sx={deliveryInputSx}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
              <DeliveryLabel htmlFor="delivery-team-size">
                {t("pages.staff.contact.team_size")}
              </DeliveryLabel>
              <TextField
                id="delivery-team-size"
                select
                fullWidth
                name="teamSize"
                value={delivery.teamSize}
                onChange={handleDeliveryChange}
                onBlur={handleBlur}
                error={touched.teamSize && !!errors.teamSize}
                helperText={touched.teamSize && errors.teamSize}
                disabled={cargando}
                required
                variant="outlined"
                sx={{
                  ...deliveryInputSx,
                  "& .MuiSelect-select": {
                    color: delivery.teamSize ? "text.primary" : "text.secondary",
                  },
                }}
                slotProps={{ select: { displayEmpty: true } }}
              >
                <MenuItem value="" disabled>
                  {t("pages.staff.contact.select_placeholder")}
                </MenuItem>
                {Array.isArray(teamSizeOptions) &&
                  teamSizeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }}>
              <DeliveryLabel htmlFor="delivery-timeline">
                {t("pages.staff.contact.timeline")}
              </DeliveryLabel>
              <TextField
                id="delivery-timeline"
                select
                fullWidth
                name="timeline"
                value={delivery.timeline}
                onChange={handleDeliveryChange}
                onBlur={handleBlur}
                error={touched.timeline && !!errors.timeline}
                helperText={touched.timeline && errors.timeline}
                disabled={cargando}
                required
                variant="outlined"
                sx={{
                  ...deliveryInputSx,
                  "& .MuiSelect-select": {
                    color: delivery.timeline ? "text.primary" : "text.secondary",
                  },
                }}
                slotProps={{ select: { displayEmpty: true } }}
              >
                <MenuItem value="" disabled>
                  {t("pages.staff.contact.select_placeholder")}
                </MenuItem>
                {Array.isArray(timelineOptions) &&
                  timelineOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
              </TextField>
            </Box>
            <Box sx={{ gridColumn: "1 / -1" }}>
              <DeliveryLabel htmlFor="delivery-extra">
                {t("pages.staff.contact.extra")}
              </DeliveryLabel>
              <TextField
                id="delivery-extra"
                fullWidth
                name="extra"
                multiline
                minRows={4}
                value={delivery.extra}
                onChange={handleDeliveryChange}
                disabled={cargando}
                slotProps={{ htmlInput: { maxLength: 1000 } }}
                variant="outlined"
                sx={deliveryInputSx}
              />
            </Box>
          </>
        ) : (
          <>
            <TextField
              fullWidth
              name="name"
              placeholder={t("contacto_form.placeholder_nombre")}
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && !!errors.name}
              helperText={touched.name && errors.name}
              disabled={cargando}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              required
              variant="outlined"
              sx={defaultFieldSx}
            />
            <TextField
              fullWidth
              name="email"
              type="email"
              placeholder={t("contacto_form.placeholder_email")}
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
              disabled={cargando}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              required
              variant="outlined"
              sx={defaultFieldSx}
            />
            <TextField
              fullWidth
              name="companyName"
              placeholder={t("contacto_form.placeholder_empresa")}
              value={formData.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.companyName && !!errors.companyName}
              helperText={touched.companyName && errors.companyName}
              disabled={cargando}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              required
              variant="outlined"
              sx={defaultFieldSx}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  p: "16px",
                  border: `1px solid ${currentPhoneError ? "#d32f2f" : "#ccc"}`,
                  borderRadius: "12px",
                  bgcolor: "white",
                  transition: "border-color 0.2s",
                  "&:focus-within": {
                    borderColor: currentPhoneError ? "#d32f2f" : "#68a8de",
                    boxShadow: currentPhoneError
                      ? "none"
                      : "0 0 0 2px rgba(104,168,222,0.2)",
                  },
                  "& .PhoneInputInput": {
                    border: "none",
                    outline: "none",
                    fontSize: "1rem",
                    width: "100%",
                    marginLeft: "10px",
                    color: "#0a1128",
                  },
                }}
              >
                <PhoneInput
                  international
                  defaultCountry="AR"
                  value={phoneValue}
                  onChange={(val) => setPhoneValue(val || "")}
                  onBlur={() => setPhoneTouched(true)}
                  disabled={cargando}
                  placeholder={t("contacto_form.placeholder_telefono")}
                  maxLength={MAX_PHONE_LENGTH}
                />
              </Box>
              {currentPhoneError && (
                <Typography
                  variant="caption"
                  sx={{ color: "#d32f2f", mt: "4px", ml: "14px" }}
                >
                  {currentPhoneError}
                </Typography>
              )}
            </Box>
            <TextField
              fullWidth
              name="message"
              multiline
              rows={4}
              placeholder={t("contacto_form.placeholder_mensaje")}
              value={formData.message}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.message && !!errors.message}
              helperText={touched.message && errors.message}
              disabled={cargando}
              slotProps={{ htmlInput: { maxLength: 1000 } }}
              required
              variant="outlined"
              sx={defaultFieldSx}
            />
          </>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-start" },
            mt: 0.5,
            gridColumn: isDelivery ? "1 / -1" : undefined,
          }}
        >
          {recaptchaSiteKey ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                "& .recaptcha-container": {
                  transform: {
                    xs: "scale(0.85)",
                    sm: "scale(1)",
                  },
                  transformOrigin: "center",
                },
              }}
            >
              <Box className="recaptcha-container">
                <ReCAPTCHA
                  key={i18n.language}
                  ref={captchaRef}
                  hl={i18n.language}
                  sitekey={recaptchaSiteKey}
                  onChange={onChange}
                />
              </Box>
            </Box>
          ) : (
            <Typography sx={{ color: "#d32f2f", fontSize: "0.875rem" }}>
              {t("contacto_form.captcha_not_configured")}
            </Typography>
          )}
        </Box>

        <Button
          fullWidth
          type="submit"
          disabled={!isFormValid() || cargando}
          variant="contained"
          endIcon={isDelivery && !cargando ? <Send /> : undefined}
          sx={
            isDelivery
              ? {
                  gridColumn: "1 / -1",
                  py: 1.5,
                  borderRadius: 1,
                  bgcolor: "primary.main",
                  color: "common.white",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "primary.dark",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "grey.200",
                    color: "text.secondary",
                    cursor: cargando ? "wait" : "not-allowed",
                  },
                }
              : {
                  py: 2,
                  borderRadius: "12px",
                  bgcolor: "#0A1128",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  textTransform: "none",
                  boxShadow: "0 10px 25px rgba(10,17,40,0.15)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "#121c3e",
                    boxShadow: "0 12px 28px rgba(10,17,40,0.25)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "#e2e8f0",
                    color: "#94a3b8",
                    cursor: cargando ? "wait" : "not-allowed",
                  },
                }
          }
        >
          {cargando
            ? t("contacto_form.enviando")
            : isDelivery
              ? t("pages.staff.contact.cta")
              : t("contacto_form.boton")}
        </Button>
      </Box>
    </Box>
  );
}
